import * as Logger from './logger'

export interface IHasID {
    getID(): string;
}

export class SyncHelper {
    static toLookup<TSrc extends IHasID>(src: TSrc[]): { [index: string]: TSrc } {
        let result: { [index: string]: TSrc } = {};

        src.forEach(i => result[i.getID()] = i);

        return result;
    }

    static syncArrayInOrder<TSrc, TDest>(
        srcArray: TSrc[], destArray: TDest[],
        createItem: (src: TSrc) => TDest,
        updateItem: (src: TSrc, dest: TDest, index: number) => void,
        deleteItem: (dest: TDest) => any
    ): { itemsRemoved: TDest[]; itemsAdded: TDest[] } {

        if (!srcArray) {
            srcArray = [];
        }

        let added: TDest[] = [];
        let removed: TDest[] = [];

        while (destArray.length > srcArray.length) {
            let itemToDelete = destArray[destArray.length - 1];
            deleteItem(itemToDelete);
            destArray.splice(destArray.length - 1, 1);
            removed.push(itemToDelete);
        }

        while (srcArray.length > destArray.length) {
            let srcItem = srcArray[destArray.length];
            let destItem = createItem(srcItem);
            destArray.push(destItem);
            added.push(destItem);
        }

        for (let i = 0; i < destArray.length; ++i) {
            updateItem(srcArray[i], destArray[i], i);
        }

        return {
            itemsAdded: added,
            itemsRemoved: removed,
        };
    }

    static syncArraysWithId<TSrc extends IHasID, TDest extends IHasID>(
        srcArray: TSrc[], destArray: TDest[],
        createItem: (src: TSrc) => TDest,
        updateItem: (src: TSrc, dest: TDest) => void,
        deleteItem: (dest: TDest) => void
    ): { itemsRemoved: TDest[]; itemsById: { [id: string]: TDest | undefined; } } {

        return SyncHelper.syncArraysWithIdWithSelector(
            srcArray, i => i.getID(),
            destArray, i => i.getID(),
            createItem,
            updateItem,
            deleteItem,
        )
    }

    static syncArraysWithIdWithSelector<TSrc, TDest>(
        srcArray: TSrc[], srcGetId: (item: TSrc) => string,
        destArray: TDest[], destGetId: (item: TDest) => string,
        createItem: (src: TSrc) => TDest,
        updateItem: (src: TSrc, dest: TDest) => void,
        deleteItem: (dest: TDest) => void
    ): { itemsRemoved: TDest[]; itemsById: { [id: string]: TDest | undefined; } } {

        let srcMap: { [id: string]: TSrc | undefined; } = {};
        for (let i = 0; i < srcArray.length; ++i) {
            let srcItem = srcArray[i];
            srcMap[srcGetId(srcItem)] = srcItem;
        }

        let destMap: { [id: string]: TDest | undefined; } = {};
        for (let i = 0; i < destArray.length; ++i) {
            let destItem = destArray[i];
            destMap[destGetId(destItem)] = destItem;
        }

        let newSrcItems: TSrc[] = [];
        let updatedSrcItems: TSrc[] = [];
        let removedDestItems: TDest[] = [];

        for (let key in srcMap) {
            const srcValue = srcMap[key];
            if (srcValue) {
                if (!(destMap[key])) {
                    newSrcItems.push(srcValue);
                } else {
                    updatedSrcItems.push(srcValue);
                }
            }
        }

        for (let key in destMap) {
            if (!(srcMap[key])) {
                const destValue = destMap[key];
                if (destValue) {
                    removedDestItems.push(destValue);
                }
            }
        }
        for (let i = 0; i < removedDestItems.length; ++i) {

            let destItem = removedDestItems[i];
            let idToRemove = destGetId(destItem);

            // default to true in case deleteItem throws.
            let shouldRemove = true;

            try {
                deleteItem(destItem);
            } catch (ex) {
                Logger.log(Logger.util_sync, "exception removing item", destItem, ":", ex);
            }

            if (shouldRemove) {
                for (let destIndex = destArray.length - 1; destIndex >= 0; --destIndex) {
                    if (destGetId(destArray[destIndex]) === idToRemove) {
                        destArray.splice(destIndex, 1);
                    }
                }
            }
        }

        for (let i = 0; i < updatedSrcItems.length; ++i) {
            let srcItem = updatedSrcItems[i];
            let destItem = destMap[srcGetId(srcItem)];

            if (destItem) {
                try {
                    updateItem(srcItem, destItem);
                } catch (ex) {
                    Logger.log(Logger.util_sync, "exception updating item", srcItem, "->", destItem, ":", ex);
                }
            }
        }

        for (let i = 0; i < newSrcItems.length; ++i) {
            let srcItem = newSrcItems[i];
            let destItem = createItem(srcItem);

            destArray.push(destItem);
            destMap[srcGetId(srcItem)] = destItem;
            try {
                updateItem(srcItem, destItem);
            } catch (ex) {
                Logger.log(Logger.util_sync, "exception updating item", srcItem, "->", destItem, ":", ex);
            }
        }

        return {
            itemsRemoved: removedDestItems,
            itemsById: destMap,
        };
    }
}
